import {Controller, useForm} from "react-hook-form";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter} from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Select, SelectTrigger, SelectContent, SelectItem, SelectValue} from "@/components/ui/select";
import PatientAutocomplete from "@/components/patients/PatientAutocomplete.tsx";
import {Doctor, Event, Patient} from "@/lib/types/models.ts";
import {EventEditFormPayload, EventUpdateSchema} from "@/lib/types/forms.ts";
import {zodResolver} from "@hookform/resolvers/zod";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {IdCard} from "lucide-react";
import {Link, usePage} from "@inertiajs/react";
import {useEffect, useState} from "react";
import DatePicker from "@/components/ui/DatePicker.tsx";

interface EditEventDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onUpdate: (data: EventEditFormPayload) => Promise<void>;
    onDelete: (event: Event) => Promise<void>;
    event: Event;
    doctors: Doctor[];
}

export default function EditEventDialog({
                                            isOpen,
                                            onOpenChange,
                                            onUpdate,
                                            onDelete,
                                            event,
                                            doctors
                                        }: EditEventDialogProps) {
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
    const {translations} = usePage().props as unknown as { translations: any };
    const t = translations.edit_dialog;

    const form = useForm<EventEditFormPayload>({
        resolver: zodResolver(EventUpdateSchema),
        defaultValues: {
            name: event.name ?? "",
            description: event.description ?? "",
            start_at: event.start_at ?? "",
            end_at: event.end_at ?? "",
            event_type: event.event_type ?? "",
            doctor_id: event.doctor_id ?? "",
            patient_id: event.patient_id ?? undefined,
            history_entry_attributes: event.history_entry
                ? {
                    id: event.history_entry.id,
                    appointment_at: event.history_entry.appointment_at ?? "",
                    work_done: event.history_entry.work_done ?? "",
                    tooth: event.history_entry.tooth ?? "",
                    bill: event.history_entry.bill ?? 0,
                    paid: event.history_entry.paid ?? false,
                    patient_card_id: event.history_entry.patient_card_id ?? undefined,
                }
                : undefined,
        },
    });

    const patientCardId = selectedPatient?.patient_card?.id ?? event.history_entry?.patient_card_id;

    useEffect(() => {
        if (!isOpen) return;

        const history = event.history_entry ?? undefined;

        form.reset({
            name: event.name ?? "",
            description: event.description ?? "",
            start_at: event.start_at ?? "",
            end_at: event.end_at ?? "",
            event_type: event.event_type ?? "",
            doctor_id: event.doctor_id ?? "",
            patient_id: event.patient_id ?? undefined,
            history_entry_attributes: history
                ? {
                    id: history.id,
                    appointment_at: history.appointment_at ?? "",
                    work_done: history.work_done ?? "",
                    tooth: history.tooth ?? "",
                    bill: history.bill != null ? Number(history.bill) : undefined,
                    paid: history.paid ?? false,
                    patient_card_id: history.patient_card_id ?? undefined,
                }
                : undefined,
        });

        if (event.patient) {
            const p: Patient = {
                ...event.patient,
                patient_card: event.patient.patient_card ?? (history?.patient_card_id ? {id: history.patient_card_id} : undefined),
            } as Patient;

            setSelectedPatient(p);
            form.setValue("patient_id", p.id);
            form.setValue("history_entry_attributes.patient_card_id", p.patient_card?.id ?? undefined);
        } else {
            setSelectedPatient(null);
            form.setValue("patient_id", undefined);
            form.setValue(
                "history_entry_attributes.appointment_at",
                event.history_entry?.appointment_at ?? ""
            )
            form.setValue("history_entry_attributes.patient_card_id", undefined);
        }
    }, [isOpen, event]);

    const handleSubmit = async (data: EventEditFormPayload) => await onUpdate(data);
    const handleDeleteClick = () => onDelete(event);

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-h-[90vh] w-full sm:max-h-[90vh] sm:max-w-lg p-4 overflow-auto"
            >
                <DialogHeader>
                    <DialogTitle>{t.title}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-3">
                        <FormField control={form.control} name="name" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.fields.name}</FormLabel>
                                <FormControl>
                                    <Input {...field} placeholder={t.placeholders.name}/>
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <FormField control={form.control} name="start_at" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.fields.start}</FormLabel>
                                <FormControl>
                                    <DatePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <FormField control={form.control} name="end_at" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.fields.end}</FormLabel>
                                <FormControl>
                                    <DatePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={t.placeholders.end}
                                    />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <FormField control={form.control} name="description" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.fields.description}</FormLabel>
                                <FormControl>
                                    <Textarea {...field} placeholder={t.placeholders.description}/>
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <Controller control={form.control} name="doctor_id" render={({field}) => (
                            <FormItem>
                                <FormLabel>{t.label_doctor}</FormLabel>
                                <FormControl>
                                    <Select
                                        value={field.value || ''}
                                        onValueChange={(val) => field.onChange(val)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder={t.placeholder_select_doctor}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {doctors.map(doc => <SelectItem key={doc.id}
                                                                            value={doc.id}>{doc.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}/>

                        <>
                            <Controller control={form.control} name="patient_id" render={({field}) => (
                                <FormItem>
                                    <FormLabel>{t.fields.patient}</FormLabel>
                                    <div className="flex items-center gap-2">
                                        <FormControl className="flex-1">
                                            <PatientAutocomplete
                                                value={selectedPatient ?? undefined}
                                                onChange={(p: Patient | null | undefined) => {
                                                    if (p) {
                                                        field.onChange(p.id);
                                                        form.setValue("name", p.name);
                                                        form.setValue("history_entry_attributes.patient_card_id", p.patient_card?.id ?? undefined);
                                                        setSelectedPatient(p);
                                                    } else {
                                                        field.onChange(undefined);
                                                        form.setValue("history_entry_attributes.patient_card_id", undefined);
                                                        setSelectedPatient(null);
                                                    }
                                                }}
                                                placeholder={t.placeholders.patient_search}
                                            />
                                        </FormControl>

                                        {patientCardId && (
                                            <Button variant="ghost" size="icon" asChild
                                                    className="hover:text-green-600">
                                                <Link href={`/patient_cards/${patientCardId}`} target="_blank">
                                                    <IdCard/>
                                                </Link>
                                            </Button>
                                        )}
                                    </div>
                                    <FormMessage/>
                                </FormItem>
                            )}/>

                            <FormField control={form.control} name="history_entry_attributes.id"
                                       render={({field}) => <Input type="hidden" {...field} />}/>
                            <FormField control={form.control} name="history_entry_attributes.patient_card_id"
                                       render={({field}) => <Input type="hidden" {...field} />}/>

                            <FormField control={form.control} name="history_entry_attributes.work_done"
                                       render={({field}) => (
                                           <FormItem>
                                               <FormLabel>{t.fields.work_done}</FormLabel>
                                               <FormControl>
                                                   <Input {...field} value={field.value ?? ""}/>
                                               </FormControl>
                                               <FormMessage/>
                                           </FormItem>
                                       )}/>

                            <FormField control={form.control} name="history_entry_attributes.tooth"
                                       render={({field}) => (
                                           <FormItem>
                                               <FormLabel>{t.fields.tooth}</FormLabel>
                                               <FormControl>
                                                   <Input {...field} value={field.value ?? ""}/>
                                               </FormControl>
                                               <FormMessage/>
                                           </FormItem>
                                       )}/>

                            <FormField control={form.control} name="history_entry_attributes.bill"
                                       render={({field}) => (
                                           <FormItem>
                                               <FormLabel>{t.fields.bill}</FormLabel>
                                               <FormControl>
                                                   <Input type="number" min={0} {...field} value={field.value ?? 0}
                                                          onChange={e => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}/>
                                               </FormControl>
                                               <FormMessage/>
                                           </FormItem>
                                       )}/>

                            <FormField control={form.control} name="history_entry_attributes.paid"
                                       render={({field}) => (
                                           <FormItem className="flex flex-row items-center space-x-3">
                                               <FormLabel>{t.fields.paid}</FormLabel>
                                               <FormControl>
                                                   <Checkbox checked={field.value ?? false}
                                                             onCheckedChange={checked => field.onChange(checked)}/>
                                               </FormControl>
                                           </FormItem>
                                       )}/>
                        </>

                        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                                <Button variant="ghost" type="button" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
                                    {t.button_close}
                                </Button>
                                <Button type="submit" className="w-full sm:w-auto">
                                    {t.buttons.update}
                                </Button>
                            </div>
                            <Button variant="destructive" type="button" onClick={handleDeleteClick} className="w-full sm:w-auto">
                                {t.button_delete}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
